/**
 * A typed table of contents — or list of figures, or list of tables — with dot leaders
 * and right-aligned page numbers.
 *
 * Input is one entry per line, its page number at the end and its level given the way
 * the heading tools read an outline (`#` marks, an outline number, or indentation):
 *
 *     Abstract  iii
 *     1 Introduction ........ 1
 *     1.1 Background  2
 *
 * Page numbers may be arabic or lowercase roman (front matter: i, ii, iii …). Uppercase
 * roman numerals are left in the title, so "World War II" stays a title.
 *
 * Two outputs. The plain text is exact: lines padded with leader dots to a chosen width,
 * for a monospaced font. The rich copy is a borderless two-column table — titles on the
 * left, page numbers right-aligned on the right — set in 12-point Times New Roman, with
 * the leader dots typed to fill each title's row. A browser cannot hand Word a real
 * leader tab that Google Docs would also keep, so the dots are counted from the
 * published Times Roman character widths, with a margin so they never wrap.
 */

import { escapeHtml } from "@/lib/tool-kit/citation-parts";
import { parseOutline } from "@/lib/tool-kit/paper-formatting/outline";

export type TocMode = "contents" | "figures" | "tables";

export interface TocEntry {
  line: number;
  level: number;
  title: string;
  /** As typed: "12" or "iv"; null when the line has none. */
  page: string | null;
}

export type TocWarningKind =
  | "missing-page"
  | "page-order"
  | "roman-after-arabic";

export interface TocWarning {
  kind: TocWarningKind;
  line: number;
  values: Record<string, string | number>;
}

export interface TocOptions {
  mode: TocMode;
  /** Figures and tables: start captions without a number with "Figure n." */
  numberCaptions: boolean;
  /** "Figure" or "Table" in the page's language. */
  captionWord: string;
}

// --- reading lines ------------------------------------------------------------------

const RE_LINES = /\r?\n/;
const RE_PAGE_AT_END =
  /^(.*?\S)(?:\s*\.{2,}\s*|\s*…+\s*|\t+|\s+)(\d{1,4}|[ivxlc]{1,8})\s*$/;
const RE_TRAILING_LEADERS = /[\s.…]+$/;
const RE_ROMAN = /^(?:c{0,3})(?:xc|xl|l?x{0,3})(?:ix|iv|v?i{0,3})$/;
const RE_DIGITS = /^\d+$/;
const ROMAN_VALUES: Record<string, number> = {
  i: 1,
  v: 5,
  x: 10,
  l: 50,
  c: 100,
};
/** Front matter runs to a few dozen pages; "mix" or "civic" is a word, not a page. */
const MAX_ROMAN_PAGE = 60;

export function romanValue(numeral: string): number | null {
  if (numeral === "" || !RE_ROMAN.test(numeral)) {
    return null;
  }
  let total = 0;
  for (let i = 0; i < numeral.length; i++) {
    const value = ROMAN_VALUES[numeral[i]];
    const next = ROMAN_VALUES[numeral[i + 1]] ?? 0;
    total += value < next ? -value : value;
  }
  return total;
}

function splitPage(line: string): { title: string; page: string | null } {
  const match = RE_PAGE_AT_END.exec(line);
  if (!match) {
    return { title: line, page: null };
  }
  const page = match[2];
  const roman = RE_DIGITS.test(page) ? null : romanValue(page);
  if (!RE_DIGITS.test(page) && (roman === null || roman > MAX_ROMAN_PAGE)) {
    return { title: line, page: null };
  }
  return { title: match[1].replace(RE_TRAILING_LEADERS, ""), page };
}

const RE_CAPTION_NUMBERED = /^(?:fig(?:ure)?\.?|table|tab\.)\s*[A-Z]?\d+/i;

function captionTitle(
  title: string,
  index: number,
  options: TocOptions
): string {
  if (!options.numberCaptions || RE_CAPTION_NUMBERED.test(title)) {
    return title;
  }
  const word = options.captionWord.trim();
  if (word !== "" && title.toLowerCase().startsWith(word.toLowerCase())) {
    return title;
  }
  return `${word} ${index + 1}. ${title}`;
}

function pageWarnings(entries: readonly TocEntry[]): TocWarning[] {
  const warnings: TocWarning[] = [];
  let lastArabic: number | null = null;
  let lastRoman: number | null = null;
  for (const entry of entries) {
    if (entry.page === null) {
      warnings.push({
        kind: "missing-page",
        line: entry.line,
        values: { title: entry.title },
      });
      continue;
    }
    const arabic = RE_DIGITS.test(entry.page) ? Number(entry.page) : null;
    const roman = arabic === null ? romanValue(entry.page) : null;
    if (roman !== null && lastArabic !== null) {
      warnings.push({
        kind: "roman-after-arabic",
        line: entry.line,
        values: { title: entry.title, page: entry.page },
      });
    }
    const previous = arabic === null ? lastRoman : lastArabic;
    const current = arabic ?? roman;
    if (current !== null && previous !== null && current < previous) {
      warnings.push({
        kind: "page-order",
        line: entry.line,
        values: { title: entry.title, page: entry.page },
      });
    }
    if (arabic !== null) {
      lastArabic = arabic;
    } else if (roman !== null) {
      lastRoman = roman;
    }
  }
  return warnings;
}

/** Entries and page warnings from typed lines. */
export function parseToc(
  text: string,
  options: TocOptions
): { entries: TocEntry[]; warnings: TocWarning[] } {
  const pages = new Map<number, string | null>();
  // Strip the page numbers first, keeping each line's indentation and position, so the
  // outline reader sees only titles and numbers its levels from the same lines.
  const titles = text.split(RE_LINES).map((raw, index) => {
    const { title, page } = splitPage(raw.trimEnd());
    pages.set(index + 1, page);
    return title;
  });
  const lines = parseOutline(titles.join("\n"));
  const entries = lines.map((line, index) => {
    const withNumber = line.number ? `${line.number} ${line.text}` : line.text;
    const isCaption = options.mode !== "contents";
    return {
      line: line.line,
      level: isCaption ? 1 : Math.max(1, line.level),
      title: isCaption ? captionTitle(withNumber, index, options) : withNumber,
      page: pages.get(line.line) ?? null,
    };
  });
  return { entries, warnings: pageWarnings(entries) };
}

// --- plain text -----------------------------------------------------------------------

export interface TocTextOptions {
  heading: string;
  /** Characters per line. */
  width: number;
  /** Spaces of indent per level below the first. */
  indent: number;
}

function wrapWords(text: string, limit: number): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of text.split(" ")) {
    if (current === "") {
      current = word;
    } else if ([...`${current} ${word}`].length <= limit) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }
  lines.push(current);
  return lines;
}

function textEntry(entry: TocEntry, options: TocTextOptions): string[] {
  const pad = " ".repeat(Math.max(0, entry.level - 1) * options.indent);
  const page = entry.page ?? "";
  // Room for the title on a line that also carries " .. page".
  const room = Math.max(10, options.width - pad.length - page.length - 4);
  const wrapped = wrapWords(entry.title, room);
  const last = wrapped.pop() ?? "";
  const lines = wrapped.map((part) => `${pad}${part}`);
  if (page === "") {
    lines.push(`${pad}${last}`);
    return lines;
  }
  const used = pad.length + [...last].length + page.length + 2;
  const dots = ".".repeat(Math.max(2, options.width - used));
  lines.push(`${pad}${last} ${dots} ${page}`);
  return lines;
}

export function tocToText(
  entries: readonly TocEntry[],
  options: TocTextOptions
): string {
  const heading = options.heading.trim();
  const title = heading
    ? [
        " ".repeat(
          Math.max(0, Math.floor((options.width - [...heading].length) / 2))
        ) + heading,
        "",
      ]
    : [];
  return [
    ...title,
    ...entries.flatMap((entry) => textEntry(entry, options)),
  ].join("\n");
}

// --- rich copy ---------------------------------------------------------------------------

/**
 * Advance widths of Times Roman, in thousandths of an em, for printable ASCII from the
 * space (32) to the tilde (126) — the published Adobe metrics, which Times New Roman
 * was drawn to match.
 */
const TIMES_ASCII = [
  250, 333, 408, 500, 500, 833, 778, 180, 333, 333, 500, 564, 250, 333, 250,
  278, 500, 500, 500, 500, 500, 500, 500, 500, 500, 500, 278, 278, 564, 564,
  564, 444, 921, 722, 667, 667, 722, 611, 556, 722, 722, 333, 389, 722, 611,
  889, 722, 722, 556, 722, 667, 556, 611, 722, 722, 944, 722, 722, 611, 333,
  278, 333, 469, 500, 333, 444, 500, 444, 500, 444, 333, 500, 500, 278, 278,
  500, 278, 778, 500, 500, 500, 500, 333, 389, 278, 500, 500, 722, 500, 500,
  444, 480, 200, 480, 541,
];
const TIMES_OTHER: Record<string, number> = {
  "–": 500,
  "—": 1000,
  "‘": 333,
  "’": 333,
  "“": 444,
  "”": 444,
  "…": 1000,
};
const RE_COMBINING = /\p{M}/gu;

const FONT_SIZE = 12;
/** 6.5 inches of text on a letter page with 1-inch margins, in points. */
const LINE = 468;
/** The page-number column. */
const PAGE_COLUMN = 36;
const TITLE_COLUMN = LINE - PAGE_COLUMN;
/** Indent per level below the first: a quarter inch. */
const LEVEL_INDENT = 18;
/** Kept clear so a font that sets a little wider than Times still fits the dots. */
const SAFETY = 12;
const DOT = (250 / 1000) * FONT_SIZE;
const SPACE = (250 / 1000) * FONT_SIZE;

function charWidth(char: string): number {
  const code = char.codePointAt(0) ?? 0;
  if (code >= 32 && code <= 126) {
    return (TIMES_ASCII[code - 32] * FONT_SIZE) / 1000;
  }
  const special = TIMES_OTHER[char];
  if (special !== undefined) {
    return (special * FONT_SIZE) / 1000;
  }
  const base = char.normalize("NFD").replace(RE_COMBINING, "");
  if (base !== char && base.length === 1) {
    return charWidth(base);
  }
  return (560 * FONT_SIZE) / 1000;
}

/** The width of `text` set in 12-point Times, in points. */
export function timesWidth(text: string): number {
  let total = 0;
  for (const char of text) {
    total += charWidth(char);
  }
  return total;
}

/** The last line of `text` wrapped into `room` points: what the leader follows. */
function lastLineWidth(text: string, room: number): number {
  let line = 0;
  for (const word of text.split(" ")) {
    const width = timesWidth(word);
    if (line === 0) {
      line = width;
    } else if (line + SPACE + width <= room) {
      line += SPACE + width;
    } else {
      line = width;
    }
  }
  return line;
}

/** The leader dots that fit after `title` in its row. */
export function leaderDots(title: string, level: number): string {
  const room = TITLE_COLUMN - (level - 1) * LEVEL_INDENT;
  const free = room - lastLineWidth(title, room) - SPACE - SAFETY;
  const count = Math.floor(free / DOT);
  return count >= 3 ? ".".repeat(count) : "";
}

const FONT = "font-family:'Times New Roman',Times,serif;font-size:12pt";
const P = "margin:0;line-height:2";

/**
 * The table of contents as a borderless two-column table for Word and Google Docs.
 * Each title sits in its own paragraph, indented by level, followed by leader dots;
 * the page number is right-aligned in the second column.
 */
export function tocToHtml(
  entries: readonly TocEntry[],
  heading: string
): string {
  const rows = entries.map((entry) => {
    const indent = (entry.level - 1) * LEVEL_INDENT;
    const dots =
      entry.page === null ? "" : leaderDots(entry.title, entry.level);
    const title = `${escapeHtml(entry.title)}${dots ? ` ${dots}` : ""}`;
    return (
      `<tr><td style="width:${TITLE_COLUMN}pt;padding:0;border:none;vertical-align:bottom">` +
      `<p style="${P};margin-left:${indent}pt">${title}</p></td>` +
      `<td style="width:${PAGE_COLUMN}pt;padding:0;border:none;vertical-align:bottom;text-align:right">` +
      `<p style="${P};text-align:right">${escapeHtml(entry.page ?? "")}</p></td></tr>`
    );
  });
  const head = heading.trim()
    ? `<p style="${P};${FONT};text-align:center"><strong>${escapeHtml(heading.trim())}</strong></p>`
    : "";
  return `${head}<table style="width:${LINE}pt;border-collapse:collapse;border:none;table-layout:fixed;${FONT}"><colgroup><col style="width:${TITLE_COLUMN}pt"><col style="width:${PAGE_COLUMN}pt"></colgroup><tbody>${rows.join("")}</tbody></table>`;
}
